/**
 * ThinkingStrip — MS284 Editorial Glyph Morph (Recognitions, Direction B).
 *
 * The Lembra AI preloader. Lives wherever the LLM is thinking — chat
 * thread mid-turn, landing parse moment, ⌘K Ask AI, anywhere we used
 * to render a three-dot wave.
 *
 * Visual: the 5-frame morphing celebration-recognition glyph (Bubble
 * → Calendar → Pot → Card → Desk) + a Newsreader italic verb that
 * changes in lockstep with the glyph frame. Each verb is paired 1:1
 * with the glyph it accompanies. Lucky's "in sync and flush" goal is
 * enforced at the animation timeline — see `pair-cycle` + `verb-cycle`
 * keyframes in src/index.css, both running on a 10s loop with the
 * same animation-delay values per pair (0s, -8s, -6s, -4s, -2s).
 *
 * MS284 changes (Lucky's design handoff for the Editorial Glyph Morph
 * + the prior MS280 "no 3-dot wave, sync icon with verb" requirement):
 *   - Verb rotation switched from the JS-driven `useRotatingVerb`
 *     hook to PURE CSS staggered animation. The hook approach drifted
 *     over time relative to the glyph's CSS animation (different
 *     timing sources, different reduced-motion handling). The design
 *     puts both on the SAME 10s `pair-cycle` keyframe with matched
 *     delays so frame N and verb N fade together — no drift possible.
 *   - Default `intervalMs` removed for the morph variant (the timing
 *     is now structural, not a prop). Sparkles variant retains the
 *     legacy JS rotation for back-compat (non-morph callers).
 *   - Verbs must come in groups of exactly 5 for the morph variant
 *     so the 1:1 pairing works. The verb pack helpers in
 *     LEMBRA_VERB_VOCAB are all length-5 to match.
 *
 * Density modes:
 *   - "default" (h-3.5 glyph, 13.5px verb) — chat surfaces
 *   - "compact" (h-3 glyph, 12px verb) — tight rows (⌘K palette)
 *
 * Drop-in: all existing callers keep working; the new morph behaviour
 * is the default, so they get the design without any prop changes.
 */

import { Sparkles } from "lucide-react";
import { useRotatingVerb } from "@/hooks/useRotatingVerb";
import { CelebrationGlyphMorph } from "@/components/lembra/CelebrationGlyph";

interface ThinkingStripProps {
  /** When false, the strip renders nothing. */
  active: boolean;
  /** Verbs to display. For the canonical morph glyph (default), exactly
   *  5 verbs are paired 1:1 with the 5 glyph frames via CSS. If a
   *  non-5 array is passed, the first 5 are used and the rest are
   *  dropped (with a dev-only console.warn). For the legacy sparkles
   *  glyph, any count works via the JS rotation hook. */
  verbs: readonly string[];
  /** ms between verb swaps. ONLY honoured for `glyph="sparkles"` —
   *  the morph variant ignores this prop because its verb cadence
   *  is locked to the glyph's CSS animation. Default 2000ms for the
   *  sparkles fallback. */
  intervalMs?: number;
  /** Density preset. */
  density?: "default" | "compact";
  /** Extra wrapper classes for layout positioning. */
  className?: string;
  /** Glyph treatment.
   *  - "morph" (default, MS284) — the 5-frame celebration-recognition
   *    morph. Verbs pair 1:1 via CSS-synced animation.
   *  - "sparkles" — Lucide Sparkles + JS-rotated verb. Kept for
   *    callers that need a non-celebration tone (admin tools, etc.). */
  glyph?: "sparkles" | "morph";
}

const MORPH_FRAME_COUNT = 5;

export function ThinkingStrip({
  active,
  verbs,
  intervalMs = 2000,
  density = "default",
  className = "",
  glyph = "morph",
}: ThinkingStripProps) {
  const isCompact = density === "compact";
  const iconClass = isCompact ? "h-3 w-3" : "h-3.5 w-3.5";
  const verbFontSize = isCompact ? "12px" : "13.5px";
  const wrapperGap = isCompact ? "gap-2" : "gap-2.5";
  const glyphColor = "text-navy/70";

  if (!active) return null;

  // ── Morph variant: CSS-synced 5-verb pairing ───────────────────
  if (glyph === "morph") {
    // Take exactly 5 verbs for the 1:1 pairing. Pad with cycled
    // values if caller passes <5 (rare; usually means a mid-rename).
    // Trim with a quiet console.warn if >5 so dev catches the misuse.
    const pairedVerbs = (() => {
      if (verbs.length === MORPH_FRAME_COUNT) return verbs;
      if (verbs.length > MORPH_FRAME_COUNT) {
        if (typeof console !== "undefined") {
          console.warn(
            `[ThinkingStrip] morph variant expects ${MORPH_FRAME_COUNT} verbs; got ${verbs.length}. Using the first ${MORPH_FRAME_COUNT}.`,
          );
        }
        return verbs.slice(0, MORPH_FRAME_COUNT);
      }
      // <5: pad by cycling. Better than crashing or rendering empty
      // slots — the morph still runs and the verb row just repeats.
      const out: string[] = [];
      for (let i = 0; i < MORPH_FRAME_COUNT; i++) {
        out.push(verbs[i % Math.max(1, verbs.length)] ?? "");
      }
      return out;
    })();

    return (
      <div
        className={`inline-flex items-center ${wrapperGap} ${className}`}
        aria-live="polite"
        aria-busy="true"
      >
        <CelebrationGlyphMorph
          size={isCompact ? 12 : 14}
          colorClass={glyphColor}
          className="shrink-0"
        />
        {/* Verb row — absolutely-positioned spans stack at the same
            baseline. Each span's `.thinking-verb-N` class carries the
            animation-delay that pairs it with `.glyph-frame-N`. */}
        <div
          className="relative flex-1"
          style={{
            height: isCompact ? 16 : 18,
            minWidth: isCompact ? 140 : 180,
          }}
        >
          {pairedVerbs.map((verb, i) => (
            <span
              key={`thinking-verb-${i}`}
              className={`thinking-verb thinking-verb-${i + 1} absolute inset-0 flex items-center`}
              style={{
                fontFamily: "Newsreader, Georgia, serif",
                fontStyle: "italic",
                fontWeight: 500,
                color: "rgba(29,52,97,0.72)",
                fontSize: verbFontSize,
                lineHeight: 1.3,
                whiteSpace: "nowrap",
              }}
            >
              {verb}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // ── Sparkles variant: legacy JS rotation, any verb count ───────
  // Kept for back-compat with non-celebration surfaces. New callers
  // should prefer the morph variant (the default).
  return <SparklesStrip
    verbs={verbs}
    intervalMs={intervalMs}
    iconClass={iconClass}
    verbFontSize={verbFontSize}
    wrapperGap={wrapperGap}
    glyphColor={glyphColor}
    className={className}
  />;
}

interface SparklesStripProps {
  verbs: readonly string[];
  intervalMs: number;
  iconClass: string;
  verbFontSize: string;
  wrapperGap: string;
  glyphColor: string;
  className: string;
}

function SparklesStrip({
  verbs,
  intervalMs,
  iconClass,
  verbFontSize,
  wrapperGap,
  glyphColor,
  className,
}: SparklesStripProps) {
  const { verb, index } = useRotatingVerb({ verbs, active: true, intervalMs });
  return (
    <div
      className={`inline-flex items-center ${wrapperGap} ${className}`}
      aria-live="polite"
      aria-busy="true"
    >
      <Sparkles className={`${iconClass} shrink-0 ${glyphColor}`} aria-hidden="true" />
      <p
        key={`thinking-verb-${index}`}
        className="verb-fade"
        style={{
          fontFamily: "Newsreader, Georgia, serif",
          fontStyle: "italic",
          fontWeight: 500,
          color: "rgba(29,52,97,0.72)",
          fontSize: verbFontSize,
          margin: 0,
          lineHeight: 1.3,
        }}
      >
        {verb}
      </p>
    </div>
  );
}

/**
 * Celebration-specific verb vocabularies per surface.
 *
 * MS284: all morph-pair vocabularies are exactly LENGTH 5 so each
 * verb pairs 1:1 with one of the 5 glyph frames via the CSS-synced
 * animation in ThinkingStrip. Frame order locked: Bubble (1) →
 * Calendar (2) → Pot (3) → Card (4) → Desk (5). Each vocab's index
 * 0 is the "received-your-brief" beat; index 4 is the "almost
 * landed" beat. Don't reorder unless the glyph order changes too.
 *
 * Sparkles-variant vocabularies (parseIntent, ambientAnswer) are
 * unchanged — those surfaces still use the JS-rotated Sparkles
 * indicator and aren't constrained to length 5.
 *
 * Period-as-punctuation across all morph verbs matches the design
 * spec (and the wordmark). Each verb ends with "." — the coral-
 * tinted full-stop is the design's brand-voice signature.
 */
export const LEMBRA_VERB_VOCAB = {
  /** SmartIntentBar parse + ⌘K Ask AI — fast Sparkles-variant calls. */
  parseIntent: ["Reading your brief", "Making sense of it"],

  /** ⌘K Ask AI + similar quick Q&A — Sparkles variant, 4 verbs / ~6s. */
  ambientAnswer: [
    "Reading your question",
    "Pulling what we know",
    "Picking the right words",
    "Double-checking",
  ],

  /** MS284 design-canonical 5-verb set paired with the morph glyph.
   *  Used by every chat-surface thinking-indicator (Chat.tsx +
   *  InlineConcierge via ThinkingBubble). Direct port from
   *  `Editorial glyph morph - Recognitions.html`. */
  conciergeRecommend: [
    "Considering your feedback.",
    "Marking the day.",
    "Cooking up options.",
    "Getting thoughtful.",
    "Almost there.",
  ],

  /** Personalised 5-verb morph-pair: drops the recipient's name into
   *  the calendar + card frames (the beats where "remembering them
   *  specifically" reads cleanest). Other 3 verbs stay generic so the
   *  rhythm feels like the design intended.
   *  Caller substitutes {name} with the recipient's first name —
   *  see ThinkingBubble.tsx for the substitution. */
  conciergeRecommendPersonalised: [
    "Considering your brief.",
    "Remembering {name}.",
    "Cooking up options.",
    "Picking what {name} would love.",
    "Almost there.",
  ],

  /** Out-of-Melbourne fallback (MS55 submit_remote_request path) —
   *  Sparkles variant; the model is composing a warm acknowledgment
   *  with no celebration anchor. 3 verbs at ~7s. */
  remoteAcknowledge: [
    "Reading your brief",
    "Logging the request",
    "Writing back",
  ],
} as const;
